import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  Button,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  useColorModeValue,
  useDisclosure,
  Box,
  Badge,
  Icon,
  Divider,
} from '@chakra-ui/react';
import { FiX, FiRefreshCw } from 'react-icons/fi';
import StageQuickPicker from './Actions/StageQuickPicker';
import ScheduleMeetingModal from './Actions/ScheduleMeetingModal';
import EmailTemplateModal from './Actions/EmailTemplateModal';
import EntitySelector from './EntitySelector';

export default function CommandPalette({ isOpen, onClose, selectedDeal: initialDeal, selectedContact: initialContact }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [showEntitySelector, setShowEntitySelector] = useState(false);
  const bg = useColorModeValue('white', 'gray.800');

  // Action modal disclosures
  const { isOpen: isStageOpen, onOpen: onStageOpen, onClose: onStageClose } = useDisclosure();
  const { isOpen: isScheduleOpen, onOpen: onScheduleOpen, onClose: onScheduleClose } = useDisclosure();
  const { isOpen: isEmailOpen, onOpen: onEmailOpen, onClose: onEmailClose } = useDisclosure();

  // Initialize selected entity from props
  useEffect(() => {
    if (initialDeal && initialContact) {
      setSelectedEntity({
        type: 'deal',
        data: initialDeal,
        contact: initialContact,
      });
      setShowEntitySelector(false);
    } else {
      setSelectedEntity(null);
      setShowEntitySelector(true);
    }
  }, [initialDeal, initialContact, isOpen]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setShowEntitySelector(false);
      setSelectedEntity(null);
    }
  }, [isOpen]);

  const commands = [
    { id: 'email', label: 'Send Email', action: onEmailOpen, description: 'Send email using templates' },
    { id: 'schedule', label: 'Schedule Meeting', action: onScheduleOpen, description: 'Set up a call or meeting' },
    { id: 'stage', label: 'Move Stage', action: onStageOpen, description: 'Update deal stage/status' },
  ];

  const filteredCommands = commands.filter((cmd) =>
    cmd.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCommandClick = (command) => {
    command.action();
    // Don't close modal - let the action modal handle it
  };

  const handleEntitySelected = (entity) => {
    setSelectedEntity(entity);
    setShowEntitySelector(false);
  };

  const handleChangeEntity = () => {
    setShowEntitySelector(true);
  };

  // Get deal and contact from selected entity
  const selectedDeal = selectedEntity?.type === 'deal' 
    ? selectedEntity.data 
    : initialDeal;
  const selectedContact = selectedEntity?.type === 'deal'
    ? selectedEntity.contact
    : selectedEntity?.type === 'contact'
    ? selectedEntity.data
    : initialContact;

  return (
    <>
      {/* Command Palette Main Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Command Palette{' '}
            <Text as="span" fontSize="sm" color="gray.500">
              (Ctrl+K)
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {/* Entity Selector or Display */}
              {showEntitySelector ? (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={3}>
                    Select a deal or contact:
                  </Text>
                  <EntitySelector
                    entityType="both"
                    onSelect={handleEntitySelected}
                    preselected={selectedEntity}
                  />
                </Box>
              ) : selectedEntity ? (
                <Box p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
                  <HStack justify="space-between">
                    <VStack align="flex-start" spacing={0}>
                      <Text fontSize="xs" fontWeight="semibold" color="blue.800">
                        {selectedEntity.type === 'deal' ? 'Selected Deal' : 'Selected Contact'}
                      </Text>
                      <Text fontWeight="semibold" fontSize="sm">
                        {selectedEntity.type === 'deal' 
                          ? selectedEntity.data.dealName 
                          : `${selectedEntity.data.firstName} ${selectedEntity.data.lastName}`
                        }
                      </Text>
                    </VStack>
                    <Button
                      size="xs"
                      leftIcon={<FiRefreshCw />}
                      onClick={handleChangeEntity}
                      variant="outline"
                      colorScheme="blue"
                    >
                      Change
                    </Button>
                  </HStack>
                </Box>
              ) : null}

              {!showEntitySelector && (
                <>
                  <Divider />

                  <Input
                    placeholder="Type a command... (schedule, move stage, email)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />

                  <VStack spacing={2} align="stretch">
                    {filteredCommands.map((cmd) => (
                      <Button
                        key={cmd.id}
                        variant="outline"
                        onClick={() => handleCommandClick(cmd)}
                        justifyContent="flex-start"
                        h="auto"
                        py={3}
                        isDisabled={!selectedEntity}
                      >
                        <VStack align="flex-start" spacing={0} w="full">
                          <Text fontWeight="semibold">{cmd.label}</Text>
                          {cmd.description && (
                            <Text fontSize="xs" color="gray.500" fontWeight="normal">
                              {cmd.description}
                            </Text>
                          )}
                        </VStack>
                      </Button>
                    ))}
                  </VStack>

                  <Text fontSize="xs" color="gray.500">
                    Tip: Keep hands on keyboard for maximum efficiency
                  </Text>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Action Modals */}
      <StageQuickPicker 
        isOpen={isStageOpen} 
        onClose={onStageClose} 
        deal={selectedDeal}
      />

      <ScheduleMeetingModal
        isOpen={isScheduleOpen}
        onClose={onScheduleClose}
        deal={selectedDeal}
        contact={selectedContact}
      />

      <EmailTemplateModal
        isOpen={isEmailOpen}
        onClose={onEmailClose}
        deal={selectedDeal}
        contact={selectedContact}
      />
    </>
  );
}

